import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import {
  type RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import {
  LoadingAnimation,
  RefreshErrorState,
  SweetpassSignedOut,
} from '@order/components';
import { type SubscriptionCreditCard, TierName } from '@order/graphql';
import {
  useSweetpassCreditCards,
  useSweetpassNextBillingDate,
  useSweetpassPlusPlans,
  useSweetpassTier,
} from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { TierUpgradeCelebrationModal } from '../../components/TierUpgradeCelebrationModal';
import type { ModalStackParamList } from '../../navigation/AppNavigation.props';
import { useSweetpassCheckoutScreenNavigation } from './hooks';
import {
  SweetpassCheckoutMultiplePlans,
  SweetpassCheckoutSinglePlan,
} from './variations';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders Sweetpass Checkout screen with support for single and multiple
 * membership plans.
 */
export const SweetpassCheckoutScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const { t } = useLocalizationContext();

  // ─── Navigation ──────────────────────────────────────────────────────

  const {
    navigateToHomeScreen,
    navigateAfterUpgrade,
    navigateToSweetpassHomeScreen,
    redirectToLoginScreen,
    redirectToJoinScreen,
  } = useSweetpassCheckoutScreenNavigation();

  const navigation = useNavigation();
  const route = useRoute<RouteProp<ModalStackParamList, 'SweetpassCheckout'>>();

  const { planId, isSwitchingPlans } = route.params ?? {};

  // ─── Membership Plans ────────────────────────────────────────────────

  const { plans: membershipPlans } = useSweetpassPlusPlans();
  const { monthly: monthlyPlan, annual: annualPlan } = membershipPlans;

  const annualPlanId = annualPlan?.id;
  const monthlyPlanId = monthlyPlan?.id;

  // ─── Feature FLags ───────────────────────────────────────────────────

  const isAnnualSubscriptionEnabled = useFeatureFlag(
    'CELS-1260-enable-annual-subscriptions',
  );

  // ─── State ───────────────────────────────────────────────────────────

  const [hasCustomerBeenUpgraded, setHasCustomerBeenUpgraded] = useState(false);
  const [selectedCreditCard, selectCreditCard] =
    useState<Partial<SubscriptionCreditCard>>();

  // ─── Current Tier ────────────────────────────────────────────────────

  const currentTierData = useSweetpassTier();

  const {
    isSweetpassTierLoading,
    sweetpassTierName,
    hasTrialMembership,
    hasUnknownTierError,
  } = currentTierData;

  // ─── Credit Cards ────────────────────────────────────────────────────

  const { creditCards, isFetchingSweetpassCreditCards } =
    useSweetpassCreditCards();

  const creditCardsData = useMemo(
    () => ({
      selectedCreditCard,
      creditCards,
      onSelectedCreditCardChange: selectCreditCard,
    }),
    [creditCards, selectedCreditCard],
  );

  // ─── Next Billing Date ───────────────────────────────────────────────

  const { nextBillingDate, isFetchingNextBillingDate } =
    useSweetpassNextBillingDate(
      !isLoggedIn || !isSwitchingPlans || !isAnnualSubscriptionEnabled,
    );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isProvidedPlanIdMatchesOneOfTheMultiplePlans =
    planId && (annualPlanId === planId || monthlyPlanId === planId);
  const isSweetpassPlusMember = sweetpassTierName === TierName.SweetpassPlus;
  const isFetchingBaseData =
    isFetchingSweetpassCreditCards || isFetchingNextBillingDate;
  const shouldRenderMultiplePlanCheckoutScreen =
    (isAnnualSubscriptionEnabled && !planId) ||
    (isAnnualSubscriptionEnabled &&
      isProvidedPlanIdMatchesOneOfTheMultiplePlans);

  // ─── Existing Sweetpass Plus Subscriber ──────────────────────────────

  // NOTE: If the customer is an existing Sweetpass Plus user (rather than just
  //       upgraded subscriber), we should redirect them because they have
  //       already been upgraded and this screen should be inaccessible for them.
  const shouldRedirectExistingSweetpassPlusSubscriber =
    !isSwitchingPlans &&
    !hasCustomerBeenUpgraded &&
    !isSweetpassTierLoading &&
    isSweetpassPlusMember;

  const redirectExistingSweetpassPlusSubscriber = useCallback(() => {
    if (!shouldRedirectExistingSweetpassPlusSubscriber) {
      return;
    }

    navigateToSweetpassHomeScreen();
  }, [
    shouldRedirectExistingSweetpassPlusSubscriber,
    navigateToSweetpassHomeScreen,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  /**
   * Redirects existing Sweetpass Plus subscribers to the Sweetpass Home screen.
   */
  useFocusEffect(redirectExistingSweetpassPlusSubscriber);

  /**
   * For now, when switching plans we'll need to overwrite the title with "Change Plan".
   */
  useLayoutEffect(() => {
    navigation.setOptions({
      title: isSwitchingPlans
        ? t('sweetpass-change-plan')
        : t('sweetpass-plus'),
    });
  }, [navigation, isSwitchingPlans, t]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SweetpassSignedOut
        onPressSignIn={redirectToLoginScreen}
        onPressJoinNow={redirectToJoinScreen}
      />
    );
  }

  // NOTE: We display a loading state for a brief second before redirecting
  //       the existing Sweetpass Plus subscriber.
  if (shouldRedirectExistingSweetpassPlusSubscriber) {
    return <LoadingAnimation />;
  }

  if (hasUnknownTierError) {
    return <RefreshErrorState />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {shouldRenderMultiplePlanCheckoutScreen ? (
        <SweetpassCheckoutMultiplePlans
          currentTierData={currentTierData}
          creditCardsData={creditCardsData}
          nextBillingDate={nextBillingDate}
          hasTrialMembership={hasTrialMembership}
          isFetchingBaseData={isFetchingBaseData}
          onUpgradeSuccess={setHasCustomerBeenUpgraded}
        />
      ) : (
        <SweetpassCheckoutSinglePlan
          currentTierData={currentTierData}
          creditCardsData={creditCardsData}
          isFetchingBaseData={isFetchingBaseData}
          onUpgradeSuccess={setHasCustomerBeenUpgraded}
        />
      )}

      <TierUpgradeCelebrationModal
        testID="sweetpass-checkout-celebration-modal"
        onDismiss={navigateToHomeScreen}
        onExplore={navigateAfterUpgrade}
        visible={hasCustomerBeenUpgraded}
      />
    </>
  );
};
